// src/core/wallet.ts
import { generateMnemonic, mnemonicToSeedSync, validateMnemonic } from "@scure/bip39";
import { wordlist as englishWordlist } from "@scure/bip39/wordlists/english";
import { HDKey } from "@scure/bip32";
import { keccak_256 } from "@noble/hashes/sha3";
import { SigningKey, getBytes } from "ethers";

// src/core/config.ts
var DEFAULT_CHAIN = {
  chainId: 137,
  name: "Polygon",
  nativeSymbol: "MATIC",
  derivationPath: `m/44'/60'/0'/0/0`
};
function resolveChainConfig(partial) {
  return {
    ...DEFAULT_CHAIN,
    ...partial
  };
}

// src/core/storage.ts
import { openDB, deleteDB } from "idb";

// src/utils/random.ts
function getRandomBytes(length) {
  if (typeof globalThis !== "undefined" && typeof globalThis.crypto !== "undefined" && typeof globalThis.crypto.getRandomValues === "function") {
    const buffer = new Uint8Array(length);
    globalThis.crypto.getRandomValues(buffer);
    return buffer;
  }
  throw new Error("Secure random number generator is not available in this environment.");
}

// src/utils/aes.ts
var MASTER_KEY_STORAGE_KEY = "walletKit.masterKey";
function getWebCrypto() {
  if (typeof globalThis !== "undefined" && globalThis.crypto) {
    return globalThis.crypto;
  }
  throw new Error("WebCrypto API not available; provide a polyfill in this environment.");
}
async function deriveMasterKey() {
  const cryptoApi = getWebCrypto();
  if (typeof sessionStorage !== "undefined") {
    const existing = sessionStorage.getItem(MASTER_KEY_STORAGE_KEY);
    if (existing) {
      return cryptoApi.subtle.importKey(
        "raw",
        hexToBytes(existing),
        { name: "AES-GCM" },
        false,
        ["encrypt", "decrypt"]
      );
    }
  }
  const raw = getRandomBytes(32);
  const key = await cryptoApi.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(MASTER_KEY_STORAGE_KEY, bytesToHex(raw));
  }
  return key;
}
async function encryptPayload(masterKey, data) {
  const cryptoApi = getWebCrypto();
  const iv = getRandomBytes(12);
  const cipherBuffer = await cryptoApi.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: toArrayBuffer(iv)
    },
    masterKey,
    toArrayBuffer(data)
  );
  return {
    iv: bytesToHex(iv),
    cipher: bytesToHex(new Uint8Array(cipherBuffer))
  };
}
async function decryptPayload(masterKey, ivHex, cipherHex) {
  const cryptoApi = getWebCrypto();
  const iv = hexToBytes(ivHex);
  const cipher = hexToBytes(cipherHex);
  const plainBuffer = await cryptoApi.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: toArrayBuffer(iv)
    },
    masterKey,
    toArrayBuffer(cipher)
  );
  return new Uint8Array(plainBuffer);
}
function bytesToHex(bytes) {
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
}
function hexToBytes(hex) {
  if (hex.length % 2 !== 0) {
    throw new Error("Invalid hex string");
  }
  const len = hex.length / 2;
  const out = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    out[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return out;
}
function toArrayBuffer(view) {
  const buffer = new ArrayBuffer(view.byteLength);
  new Uint8Array(buffer).set(view);
  return buffer;
}

// src/core/storage.ts
var DB_NAME = "wallet-kit";
var STORE_NAME = "wallets";
var dbPromise = null;
async function getDb() {
  if (typeof indexedDB === "undefined") {
    throw new Error("IndexedDB is not available");
  }
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, 1, {
      upgrade(database) {
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          database.createObjectStore(STORE_NAME, { keyPath: "id" });
        }
      }
    });
  }
  return dbPromise;
}
function getExpiry(ttl) {
  const millis = typeof ttl === "number" ? ttl : 24 * 60 * 60 * 1e3;
  return Date.now() + millis;
}
async function secureStore(wallet, ttl) {
  const db = await getDb();
  const masterKey = await deriveMasterKey();
  const payload = JSON.stringify(wallet);
  const encrypted = await encryptPayload(masterKey, new TextEncoder().encode(payload));
  const record = {
    id: wallet.id,
    cipher: encrypted.cipher,
    iv: encrypted.iv,
    expiresAt: getExpiry(ttl)
  };
  await db.put(STORE_NAME, record);
}
async function readStored(walletId) {
  const db = await getDb();
  const result = await db.get(STORE_NAME, walletId);
  if (!result) {
    return null;
  }
  if (Date.now() > result.expiresAt) {
    await db.delete(STORE_NAME, walletId);
    return null;
  }
  const masterKey = await deriveMasterKey();
  const plain = await decryptPayload(masterKey, result.iv, result.cipher);
  const record = JSON.parse(new TextDecoder().decode(plain));
  return record;
}
async function wipeStored(walletId) {
  const db = await getDb();
  await db.delete(STORE_NAME, walletId);
}
async function hasWallet(walletId) {
  try {
    const db = await getDb();
    const result = await db.get(STORE_NAME, walletId);
    if (!result) {
      return false;
    }
    if (Date.now() > result.expiresAt) {
      await db.delete(STORE_NAME, walletId);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}
async function hashWalletId(address) {
  const normalized = address.toLowerCase();
  const encoder = new TextEncoder();
  const data = encoder.encode(normalized);
  const digest = await sha256(data);
  const hashHex = bytesToHex(new Uint8Array(digest));
  return `wallet_${hashHex}`;
}
async function sha256(data) {
  if (typeof globalThis !== "undefined" && globalThis.crypto && globalThis.crypto.subtle) {
    return globalThis.crypto.subtle.digest("SHA-256", toArrayBuffer(data));
  }
  throw new Error("SHA-256 digest unavailable; provide WebCrypto in this environment.");
}

// src/core/emoji-pin.ts
var SAFE_EMOJI = [
  "\u{1F3A8}",
  "\u{1F308}",
  "\u{1F3AD}",
  "\u{1F3B8}",
  "\u{1F3AF}",
  "\u{1F3B2}",
  "\u{1F3B0}",
  "\u{1F3AA}",
  "\u{1F3A1}",
  "\u{1F3A2}",
  "\u{1F355}",
  "\u{1F354}",
  "\u{1F37F}",
  "\u{1F369}",
  "\u{1F36A}",
  "\u{1F36B}",
  "\u26BD",
  "\u{1F3C0}",
  "\u{1F3C8}",
  "\u26BE",
  "\u{1F3BE}",
  "\u{1F3D0}",
  "\u{1F3B1}",
  "\u{1F3D3}",
  "\u{1F680}",
  "\u2728",
  "\u{1F525}",
  "\u{1F48E}",
  "\u{1F31F}",
  "\u2B50"
];
var DEFAULT_PIN_LENGTH = 4;
var PBKDF2_ITERATIONS = 5e5;
function generateEmojiPin(length = DEFAULT_PIN_LENGTH) {
  if (length <= 0) {
    throw new Error("Emoji PIN length must be positive");
  }
  const randomBytes = getRandomBytes(length);
  const emoji = [];
  for (let i = 0; i < length; i += 1) {
    const index = randomBytes[i] % SAFE_EMOJI.length;
    emoji.push(SAFE_EMOJI[index]);
  }
  return emoji.join("");
}
function validateEmojiPin(pin, expectedLength = DEFAULT_PIN_LENGTH) {
  if (!pin) {
    return false;
  }
  const emojiArray = Array.from(pin);
  if (emojiArray.length !== expectedLength) {
    return false;
  }
  return emojiArray.every((emoji) => SAFE_EMOJI.includes(emoji));
}

// src/core/emoji-encryption.ts
var SALT_LENGTH = 16;
var IV_LENGTH = 12;
function getWebCrypto2() {
  if (typeof globalThis !== "undefined" && globalThis.crypto) {
    return globalThis.crypto;
  }
  throw new Error("WebCrypto API not available; please provide a polyfill for this environment.");
}
function assertPin(emojiPin) {
  if (typeof emojiPin !== "string" || Array.from(emojiPin).length < DEFAULT_PIN_LENGTH) {
    throw new Error("Emoji PIN is required");
  }
}
async function encryptWithEmojiPin(mnemonic, emojiPin, metadata) {
  if (!mnemonic) {
    throw new Error("Mnemonic is required for emoji PIN encryption");
  }
  assertPin(emojiPin);
  const cryptoApi = getWebCrypto2();
  const encoder = new TextEncoder();
  const payload = {
    mnemonic,
    metadata: {
      chain: metadata?.chain || "base_sepolia",
      bgStyle: metadata?.bgStyle || 0,
      privateMessage: metadata?.privateMessage || ""
    }
  };
  const salt = getRandomBytes(SALT_LENGTH);
  const iv = getRandomBytes(IV_LENGTH);
  const pinKey = await cryptoApi.subtle.importKey(
    "raw",
    encoder.encode(emojiPin),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  const aesKey = await cryptoApi.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: toArrayBuffer(salt),
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256"
    },
    pinKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"]
  );
  const cipherBuffer = await cryptoApi.subtle.encrypt(
    { name: "AES-GCM", iv: toArrayBuffer(iv) },
    aesKey,
    toArrayBuffer(encoder.encode(JSON.stringify(payload)))
  );
  const ciphertext = new Uint8Array(cipherBuffer);
  const result = new Uint8Array(SALT_LENGTH + IV_LENGTH + ciphertext.length);
  result.set(salt, 0);
  result.set(iv, SALT_LENGTH);
  result.set(ciphertext, SALT_LENGTH + IV_LENGTH);
  return result;
}
async function decryptWithEmojiPin(encrypted, emojiPin) {
  if (!encrypted || encrypted.length <= SALT_LENGTH + IV_LENGTH) {
    throw new Error("Encrypted payload is malformed");
  }
  assertPin(emojiPin);
  const cryptoApi = getWebCrypto2();
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();
  const salt = encrypted.slice(0, SALT_LENGTH);
  const iv = encrypted.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
  const ciphertext = encrypted.slice(SALT_LENGTH + IV_LENGTH);
  const pinKey = await cryptoApi.subtle.importKey(
    "raw",
    encoder.encode(emojiPin),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  const aesKey = await cryptoApi.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: toArrayBuffer(salt),
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256"
    },
    pinKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"]
  );
  try {
    const plaintext = await cryptoApi.subtle.decrypt(
      { name: "AES-GCM", iv: toArrayBuffer(iv) },
      aesKey,
      toArrayBuffer(ciphertext)
    );
    const decryptedString = decoder.decode(plaintext);
    try {
      const payload = JSON.parse(decryptedString);
      if (payload.mnemonic && payload.metadata) {
        return payload;
      }
    } catch {
      return {
        mnemonic: decryptedString,
        metadata: {
          chain: "base_sepolia",
          bgStyle: 0,
          privateMessage: ""
        }
      };
    }
    throw new Error("Invalid payload structure");
  } catch (error) {
    if (error instanceof Error && error.message === "Invalid payload structure") {
      throw error;
    }
    throw new Error("Invalid PIN or corrupted data");
  }
}

// src/utils/base58.ts
var ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
var BASE = ALPHABET.length;
var LEADER = ALPHABET[0];
var ALPHABET_MAP = (() => {
  const map = {};
  for (let i = 0; i < BASE; i++) {
    const char = ALPHABET[i];
    if (map[char] !== void 0) {
      throw new Error(`Ambiguous character ${char} in base58 alphabet`);
    }
    map[char] = i;
  }
  return map;
})();
function encodeBase58(data) {
  if (!(data instanceof Uint8Array)) {
    throw new Error("Base58 encode expects Uint8Array input");
  }
  if (data.length === 0) {
    return "";
  }
  let zeroes = 0;
  while (zeroes < data.length && data[zeroes] === 0) {
    zeroes++;
  }
  const size = (data.length - zeroes) * Math.log(256) / Math.log(BASE) + 1 >>> 0;
  const digits = new Array(size).fill(0);
  let length = 0;
  for (let i = zeroes; i < data.length; i++) {
    let carry = data[i];
    let j = 0;
    for (let k = size - 1; (carry !== 0 || j < length) && k >= 0; k--, j++) {
      carry += digits[k] << 8;
      digits[k] = carry % BASE;
      carry = carry / BASE | 0;
    }
    length = j;
  }
  let idx = size - length;
  while (idx < size && digits[idx] === 0) {
    idx++;
  }
  let encoded = LEADER.repeat(zeroes);
  for (; idx < size; idx++) {
    encoded += ALPHABET[digits[idx]];
  }
  return encoded;
}
function decodeBase58(encoded) {
  if (typeof encoded !== "string" || encoded.length === 0) {
    throw new Error("Base58 decode expects non-empty string input");
  }
  try {
    let zeroes = 0;
    while (zeroes < encoded.length && encoded[zeroes] === LEADER) {
      zeroes++;
    }
    const size = (encoded.length - zeroes) * Math.log(BASE) / Math.log(256) + 1 >>> 0;
    const bytes = new Array(size).fill(0);
    let length = 0;
    for (let i = zeroes; i < encoded.length; i++) {
      const value = ALPHABET_MAP[encoded[i]];
      if (value === void 0) {
        throw new Error(`Invalid base58 character "${encoded[i]}" at position ${i}`);
      }
      let carry = value;
      let j = 0;
      for (let k = size - 1; (carry !== 0 || j < length) && k >= 0; k--, j++) {
        carry += bytes[k] * BASE;
        bytes[k] = carry & 255;
        carry >>= 8;
      }
      length = j;
    }
    let idx = size - length;
    while (idx < size && bytes[idx] === 0) {
      idx++;
    }
    const output = new Uint8Array(zeroes + (size - idx));
    output.fill(0, 0, zeroes);
    let writeIndex = zeroes;
    for (; idx < size; idx++) {
      output[writeIndex++] = bytes[idx];
    }
    return output;
  } catch (error) {
    throw new Error("Failed to decode base58 string");
  }
}

// src/core/wallet.ts
function deriveWallet(seed, derivationPath) {
  const hd = HDKey.fromMasterSeed(seed);
  const child = hd.derive(derivationPath);
  if (!child.privateKey) {
    throw new Error("Failed to derive private key from mnemonic");
  }
  const privateKey = child.privateKey;
  const signingKey = new SigningKey(privateKey);
  const publicKey = getBytes(signingKey.publicKey).slice(1);
  const addressBytes = keccak_256(publicKey).slice(-20);
  const address = `0x${bytesToHex(addressBytes)}`;
  return {
    privateKey,
    address
  };
}
function normalizeMnemonic(input) {
  return input.trim().toLowerCase().replace(/\s+/g, " ");
}
async function generateWallet(config, tenantId, meta) {
  const chain = resolveChainConfig(config);
  const mnemonic = generateMnemonic(englishWordlist, 128);
  const seed = mnemonicToSeedSync(mnemonic);
  const { privateKey, address } = deriveWallet(seed, chain.derivationPath);
  const id = await hashWalletId(address);
  return {
    id,
    address,
    privateKey: `0x${bytesToHex(privateKey)}`,
    mnemonic,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    tenantId,
    meta
  };
}
async function importMnemonic(mnemonic, config, tenantId, meta) {
  const chain = resolveChainConfig(config);
  const normalized = normalizeMnemonic(mnemonic);
  if (!validateMnemonic(normalized, englishWordlist)) {
    throw new Error("Invalid mnemonic provided");
  }
  const seed = mnemonicToSeedSync(normalized);
  const { privateKey, address } = deriveWallet(seed, chain.derivationPath);
  const id = await hashWalletId(address);
  return {
    id,
    address,
    privateKey: `0x${bytesToHex(privateKey)}`,
    mnemonic: normalized,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    tenantId,
    meta
  };
}
async function createWalletWithClaim(options) {
  const {
    chain = "base_sepolia",
    metadata,
    baseUrl,
    assetId,
    pinLength = 4
  } = options || {};
  const wallet = await generateWallet({ name: chain });
  const emojiPin = generateEmojiPin(pinLength);
  const walletMetadata = {
    chain,
    bgStyle: metadata?.bgStyle || 0,
    privateMessage: metadata?.privateMessage || ""
  };
  const encryptedMnemonic = await encryptWithEmojiPin(wallet.mnemonic, emojiPin, walletMetadata);
  const encodedPayload = encodeBase58(encryptedMnemonic);
  const base = baseUrl || (typeof window !== "undefined" ? window.location.origin : "https://app.tokenbox.io");
  const claimPath = assetId ? `/claim/${assetId}` : "/claim";
  const claimUrl = `${base}${claimPath}#${encodedPayload}`;
  return {
    wallet,
    claimUrl,
    emojiPin
  };
}

// src/core/mnemonic.ts
var PBKDF2_ITERATIONS2 = 21e4;
async function exportMnemonic(wallet, opts) {
  if (!wallet.mnemonic) {
    throw new Error("Mnemonic not available for this wallet");
  }
  if (!opts?.password) {
    return wallet.mnemonic;
  }
  const cryptoApi = getCrypto();
  const encoder = new TextEncoder();
  const passwordKey = await cryptoApi.subtle.importKey("raw", encoder.encode(opts.password), "PBKDF2", false, ["deriveKey"]);
  const salt = getRandomBytes(16);
  const saltBuffer = toArrayBuffer(salt);
  const aesKey = await cryptoApi.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: saltBuffer,
      iterations: PBKDF2_ITERATIONS2,
      hash: "SHA-256"
    },
    passwordKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"]
  );
  const iv = getRandomBytes(12);
  const ivBuffer = toArrayBuffer(iv);
  const plaintext = encoder.encode(wallet.mnemonic);
  const cipherBuffer = await cryptoApi.subtle.encrypt(
    { name: "AES-GCM", iv: ivBuffer },
    aesKey,
    toArrayBuffer(plaintext)
  );
  const payload = {
    version: 1,
    cipher: bytesToHex(new Uint8Array(cipherBuffer)),
    iv: bytesToHex(iv),
    salt: bytesToHex(salt),
    iterations: PBKDF2_ITERATIONS2
  };
  return `encrypted:${toBase64(JSON.stringify(payload))}`;
}
function getCrypto() {
  if (typeof globalThis !== "undefined" && globalThis.crypto) {
    return globalThis.crypto;
  }
  throw new Error("WebCrypto API not available; provide a polyfill in this environment.");
}
function toBase64(value) {
  if (typeof btoa === "function") {
    return btoa(value);
  }
  if (typeof Buffer !== "undefined") {
    return Buffer.from(value, "utf8").toString("base64");
  }
  throw new Error("Base64 encoding not available.");
}

// src/core/events.ts
import EventEmitter from "eventemitter3";
var emitter = new EventEmitter();
function onEvent(event, handler) {
  emitter.on(event, handler);
  return () => emitter.off(event, handler);
}
function emitEvent(event) {
  emitter.emit(event.type, event.payload);
}

// src/core/helpers.ts
import QRCode from "qrcode";
function getAddress(wallet) {
  return wallet.address;
}
async function generateQr(data, options) {
  return QRCode.toDataURL(data, {
    width: options?.size || 256,
    errorCorrectionLevel: options?.errorCorrectionLevel || "M",
    margin: 1
  });
}

// src/core/import.ts
import { HDNodeWallet, getAddress as getAddress2 } from "ethers";
import { validateMnemonic as validateMnemonic2 } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english";
async function importFromMnemonic(mnemonic) {
  const cleanMnemonic = mnemonic.trim().toLowerCase();
  if (!validateMnemonic2(cleanMnemonic, wordlist)) {
    throw new Error("Invalid mnemonic phrase");
  }
  const wallet = HDNodeWallet.fromPhrase(cleanMnemonic);
  return {
    wallet,
    address: wallet.address,
    mnemonic: cleanMnemonic
  };
}
function validateAddress(address) {
  const value = address?.trim();
  if (!value) {
    return false;
  }
  try {
    getAddress2(value);
    return true;
  } catch {
    return false;
  }
}
function sanitizeMnemonic(input) {
  return input.trim().toLowerCase().replace(/\s+/g, " ");
}

// src/core/transfer.ts
import { JsonRpcProvider, formatEther, parseEther, toBigInt } from "ethers";
var DEFAULT_RPC_URLS = {
  sepolia: "https://ethereum-sepolia-rpc.publicnode.com",
  holesky: "https://ethereum-holesky-rpc.publicnode.com",
  sepoliaBackup: "https://rpc.ankr.com/eth_sepolia"
};
async function getBalance(wallet, rpcUrl = DEFAULT_RPC_URLS.sepolia) {
  const provider = new JsonRpcProvider(rpcUrl);
  const connectedWallet = wallet.connect(provider);
  const balanceWei = await provider.getBalance(connectedWallet.address);
  const balance = formatEther(balanceWei);
  return { balance, balanceWei };
}
async function estimateGas(wallet, _toAddress, rpcUrl = DEFAULT_RPC_URLS.sepolia) {
  const provider = new JsonRpcProvider(rpcUrl);
  wallet.connect(provider);
  const feeData = await provider.getFeeData();
  const maxFeePerGas = feeData.maxFeePerGas || feeData.gasPrice || parseEther("0.00000002");
  const gasLimit = toBigInt(21e3);
  const baseGasCost = gasLimit * maxFeePerGas;
  const gasCostWithBuffer = baseGasCost * toBigInt(120) / toBigInt(100);
  return gasCostWithBuffer;
}
async function transferAll(wallet, toAddress, rpcUrl = DEFAULT_RPC_URLS.sepolia) {
  const provider = new JsonRpcProvider(rpcUrl);
  const connectedWallet = wallet.connect(provider);
  const balanceWei = await provider.getBalance(connectedWallet.address);
  if (balanceWei === 0n) {
    throw new Error("Wallet balance is zero");
  }
  console.log(`\u{1F4B0} Balance: ${formatEther(balanceWei)} ETH`);
  const feeData = await provider.getFeeData();
  const maxFeePerGas = feeData.maxFeePerGas || feeData.gasPrice || parseEther("0.00000002");
  const maxPriorityFeePerGas = feeData.maxPriorityFeePerGas || parseEther("0.000000001");
  console.log(`\u26FD Max Fee Per Gas: ${formatEther(maxFeePerGas)} ETH`);
  const gasLimit = toBigInt(21e3);
  const maxGasCost = gasLimit * maxFeePerGas * toBigInt(130) / toBigInt(100);
  console.log(`\u{1F4B8} Estimated gas cost: ${formatEther(maxGasCost)} ETH`);
  const amountToSend = balanceWei - maxGasCost;
  if (amountToSend <= 0n) {
    const requiredBalance = formatEther(maxGasCost);
    const currentBalance = formatEther(balanceWei);
    throw new Error(
      `Insufficient balance to cover gas fees. Need at least ${requiredBalance} ETH for gas, but only have ${currentBalance} ETH total.`
    );
  }
  console.log(`\u{1F4E4} Sending: ${formatEther(amountToSend)} ETH`);
  const tx = await connectedWallet.sendTransaction({
    to: toAddress,
    value: amountToSend,
    gasLimit,
    maxFeePerGas,
    maxPriorityFeePerGas
  });
  console.log(`\u23F3 Transaction sent: ${tx.hash}`);
  console.log("\u23F3 Waiting for confirmation...");
  const receipt = await tx.wait(1);
  if (!receipt) {
    throw new Error("Transaction failed - no receipt");
  }
  if (receipt.status === 0) {
    throw new Error("Transaction reverted");
  }
  console.log(`\u2705 Transaction confirmed in block ${receipt.blockNumber}`);
  const actualGasUsed = receipt.gasUsed * (receipt.gasPrice || maxFeePerGas);
  const actualAmountSent = amountToSend;
  console.log(`\u{1F4B5} Actual gas used: ${formatEther(actualGasUsed)} ETH`);
  console.log(`\u2713 Transfer complete: ${formatEther(actualAmountSent)} ETH`);
  return {
    txHash: receipt.hash,
    amount: formatEther(actualAmountSent),
    from: connectedWallet.address,
    to: toAddress,
    timestamp: Date.now(),
    blockNumber: receipt.blockNumber,
    currency: "ETH"
  };
}

// src/core/erc20.ts
import { JsonRpcProvider as JsonRpcProvider2, Contract, formatUnits, parseUnits, toBigInt as toBigInt2 } from "ethers";
var ERC20_ABI = [
  // Read functions
  "function balanceOf(address owner) view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)",
  "function name() view returns (string)",
  // Write functions
  "function transfer(address to, uint256 amount) returns (bool)"
];
async function getTokenBalance(wallet, tokenAddress, rpcUrl) {
  const provider = new JsonRpcProvider2(rpcUrl);
  const contract = new Contract(tokenAddress, ERC20_ABI, provider);
  try {
    const [balanceWei, decimals, symbol] = await Promise.all([
      contract.balanceOf(wallet.address),
      contract.decimals(),
      contract.symbol()
    ]);
    const balance = formatUnits(balanceWei, decimals);
    return {
      balance,
      balanceWei,
      decimals,
      symbol
    };
  } catch (error) {
    console.error("Failed to get token balance:", error);
    throw new Error(`Failed to get token balance: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
}
async function transferToken(wallet, tokenAddress, toAddress, rpcUrl) {
  const provider = new JsonRpcProvider2(rpcUrl);
  const connectedWallet = wallet.connect(provider);
  const contract = new Contract(tokenAddress, ERC20_ABI, connectedWallet);
  try {
    const [balanceWei, decimals, symbol] = await Promise.all([
      contract.balanceOf(wallet.address),
      contract.decimals(),
      contract.symbol()
    ]);
    if (balanceWei === 0n) {
      throw new Error(`No ${symbol} tokens to transfer`);
    }
    console.log(`=\uFFFD Token Balance: ${formatUnits(balanceWei, decimals)} ${symbol}`);
    const ethBalance = await provider.getBalance(wallet.address);
    console.log(`\uFFFD ETH for gas: ${formatUnits(ethBalance, 18)} ETH`);
    const feeData = await provider.getFeeData();
    const maxFeePerGas = feeData.maxFeePerGas || feeData.gasPrice || parseUnits("0.00000002", "ether");
    const maxPriorityFeePerGas = feeData.maxPriorityFeePerGas || parseUnits("0.000000001", "ether");
    const estimatedGas = await contract.transfer.estimateGas(toAddress, balanceWei);
    const gasLimit = estimatedGas * toBigInt2(130) / toBigInt2(100);
    console.log(`\uFFFD Estimated gas: ${gasLimit.toString()}`);
    const gasCost = gasLimit * maxFeePerGas;
    console.log(`=\uFFFD Estimated gas cost: ${formatUnits(gasCost, 18)} ETH`);
    if (ethBalance < gasCost) {
      throw new Error(
        `Insufficient ETH for gas. Need ${formatUnits(gasCost, 18)} ETH but only have ${formatUnits(ethBalance, 18)} ETH`
      );
    }
    console.log(`=\uFFFD Transferring all ${formatUnits(balanceWei, decimals)} ${symbol}`);
    const tx = await contract.transfer(toAddress, balanceWei, {
      gasLimit,
      maxFeePerGas,
      maxPriorityFeePerGas
    });
    console.log(`\uFFFD Transaction sent: ${tx.hash}`);
    console.log("\uFFFD Waiting for confirmation...");
    const receipt = await tx.wait(1);
    if (!receipt) {
      throw new Error("Transaction failed - no receipt");
    }
    if (receipt.status === 0) {
      throw new Error("Transaction reverted");
    }
    console.log(` Transaction confirmed in block ${receipt.blockNumber}`);
    console.log(` Transfer complete: ${formatUnits(balanceWei, decimals)} ${symbol}`);
    return {
      txHash: receipt.hash,
      amount: formatUnits(balanceWei, decimals),
      from: wallet.address,
      to: toAddress,
      timestamp: Date.now(),
      blockNumber: receipt.blockNumber,
      currency: symbol
    };
  } catch (error) {
    console.error("Token transfer failed:", error);
    throw error instanceof Error ? error : new Error("Token transfer failed");
  }
}
async function isERC20Contract(tokenAddress, rpcUrl) {
  try {
    const provider = new JsonRpcProvider2(rpcUrl);
    const contract = new Contract(tokenAddress, ERC20_ABI, provider);
    await contract.decimals();
    return true;
  } catch {
    return false;
  }
}

// src/core/gaslessWithdraw.ts
import { JsonRpcProvider as JsonRpcProvider3, Contract as Contract2 } from "ethers";
function getEIP712Domain(chainId) {
  return {
    name: "CryptoVault Paymaster",
    version: "1",
    chainId,
    verifyingContract: "0x0000000000000000000000000000000000000000"
  };
}
var WITHDRAW_INTENT_TYPES = {
  WithdrawIntent: [
    { name: "sourceAddress", type: "address" },
    { name: "destination", type: "address" },
    { name: "token", type: "address" },
    { name: "amount", type: "uint256" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" }
  ]
};
async function signWithdrawIntent(wallet, chainId, intent) {
  const domain = getEIP712Domain(chainId);
  const types = WITHDRAW_INTENT_TYPES;
  const signature = await wallet.signTypedData(domain, types, intent);
  return signature;
}
async function getNextNonce(address, paymasterUrl) {
  const response = await fetch(`${paymasterUrl}/api/withdraw/nonce/${address}`);
  if (!response.ok) {
    throw new Error(`Failed to get nonce: ${response.statusText}`);
  }
  const data = await response.json();
  return data.nextNonce;
}
async function getPaymasterAddress(paymasterUrl) {
  const response = await fetch(`${paymasterUrl}/api/info/paymaster-address`);
  if (!response.ok) {
    throw new Error("Failed to get paymaster address");
  }
  const data = await response.json();
  if (!data.address) {
    throw new Error("Paymaster address missing in response");
  }
  return data.address;
}
async function signPermit(wallet, tokenAddress, spender, value, deadline, chainId, rpcUrl) {
  const provider = new JsonRpcProvider3(rpcUrl);
  const tokenContract = new Contract2(
    tokenAddress,
    [
      "function nonces(address owner) view returns (uint256)",
      "function name() view returns (string)",
      "function version() view returns (string)"
    ],
    provider
  );
  const tokenName = await tokenContract.name().catch(() => "Mock USDC");
  let tokenVersion;
  try {
    tokenVersion = await tokenContract.version();
  } catch {
    tokenVersion = "1";
  }
  let nonce;
  try {
    nonce = await tokenContract.nonces(wallet.address);
  } catch (error) {
    console.error("Failed to fetch permit nonce:", error);
    throw new Error("Token contract does not support EIP-2612 permits (nonces call failed)");
  }
  console.log(`   Permit nonce from contract: ${nonce}`);
  const domain = {
    name: tokenName,
    version: tokenVersion,
    chainId,
    verifyingContract: tokenAddress
  };
  const types = {
    Permit: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
      { name: "value", type: "uint256" },
      { name: "nonce", type: "uint256" },
      { name: "deadline", type: "uint256" }
    ]
  };
  const message = {
    owner: wallet.address,
    spender,
    value: value.toString(),
    nonce: Number(nonce),
    deadline
  };
  console.log("\u{1F4DD} Signing Permit...");
  console.log("   Domain:", domain);
  console.log("   Message:", message);
  const signature = await wallet.signTypedData(domain, types, message);
  return signature;
}
async function executeGaslessWithdrawal(options) {
  const { wallet, destination, tokenAddress, amount, chainId, chain, token, paymasterUrl, rpcUrl } = options;
  try {
    console.log("\u{1F522} Getting nonce from paymaster...");
    const nonce = await getNextNonce(wallet.address, paymasterUrl);
    console.log(`   Nonce: ${nonce}`);
    const deadline = Math.floor(Date.now() / 1e3) + 600;
    console.log(`   Deadline: ${deadline}`);
    const intent = {
      sourceAddress: wallet.address,
      destination,
      token: tokenAddress,
      amount,
      nonce,
      deadline
    };
    console.log("\u270D\uFE0F  Signing withdrawal intent...");
    console.log(`   Source: ${intent.sourceAddress}`);
    console.log(`   Destination: ${intent.destination}`);
    console.log(`   Token: ${intent.token}`);
    console.log(`   Amount: ${intent.amount}`);
    const signature = await signWithdrawIntent(wallet, chainId, intent);
    console.log(`   \u2705 Withdrawal intent signature created: ${signature.slice(0, 20)}...`);
    const paymasterWallet = await getPaymasterAddress(paymasterUrl);
    console.log("\u{1F510} Signing EIP-2612 Permit...");
    const permitSignature = await signPermit(
      wallet,
      tokenAddress,
      paymasterWallet,
      BigInt(amount),
      deadline,
      chainId,
      rpcUrl
    );
    console.log(`   \u2705 Permit signature created: ${permitSignature.slice(0, 20)}...`);
    console.log("\u{1F4E4} Sending to paymaster service...");
    const response = await fetch(`${paymasterUrl}/api/withdraw`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        sourceAddress: wallet.address,
        destination,
        token,
        chain,
        signature,
        nonce,
        deadline,
        permitSignature
      })
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    const result = await response.json();
    console.log("\u2705 Withdrawal successful!");
    console.log(`   TX Hash: ${result.transactions.transfer.hash}`);
    console.log(`   Amount sent: ${result.amounts.net} ${token.toUpperCase()}`);
    console.log(`   Protocol fee: ${result.amounts.fee} ${token.toUpperCase()}`);
    return {
      success: true,
      txHash: result.transactions.transfer.hash,
      amounts: result.amounts
    };
  } catch (error) {
    console.error("\u274C Gasless withdrawal failed:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
}

// src/core/assets.ts
var SUPPORTED_NETWORKS = [
  {
    id: "localhost",
    name: "Localhost (Hardhat)",
    chainId: 31337,
    category: "local",
    rpcUrl: "http://127.0.0.1:8545"
  },
  {
    id: "ethereum",
    name: "Ethereum Mainnet",
    chainId: 1,
    category: "mainnet",
    rpcUrl: "https://ethereum.publicnode.com",
    explorerUrl: "https://etherscan.io"
  },
  {
    id: "sepolia",
    name: "Ethereum Sepolia",
    chainId: 11155111,
    category: "testnet",
    rpcUrl: "https://ethereum-sepolia-rpc.publicnode.com",
    explorerUrl: "https://sepolia.etherscan.io"
  },
  {
    id: "base",
    name: "Base Mainnet",
    chainId: 8453,
    category: "mainnet",
    rpcUrl: "https://mainnet.base.org",
    explorerUrl: "https://basescan.org"
  },
  {
    id: "base_sepolia",
    name: "Base Sepolia",
    chainId: 84532,
    category: "testnet",
    rpcUrl: "https://sepolia.base.org",
    explorerUrl: "https://sepolia.basescan.org"
  },
  {
    id: "bsc",
    name: "BNB Smart Chain",
    chainId: 56,
    category: "mainnet",
    rpcUrl: "https://bsc-dataseed.binance.org",
    explorerUrl: "https://bscscan.com"
  },
  {
    id: "bsc_testnet",
    name: "BNB Smart Chain Testnet",
    chainId: 97,
    category: "testnet",
    rpcUrl: "https://data-seed-prebsc-1-s1.binance.org:8545",
    explorerUrl: "https://testnet.bscscan.com"
  }
];
var SUPPORTED_ASSETS = [
  {
    id: "eth",
    name: "Ethereum",
    code: "ETH",
    symbol: "ETH",
    type: "native",
    decimals: 18,
    color: "#627eea",
    description: "Native Ethereum currency",
    networks: [
      { networkId: "localhost", decimals: 18 },
      { networkId: "ethereum", decimals: 18 },
      { networkId: "sepolia", decimals: 18 }
    ]
  },
  {
    id: "bsc",
    name: "BNB Smart Chain",
    code: "BSC",
    symbol: "BNB",
    type: "native",
    decimals: 18,
    color: "#F0B90B",
    description: "BNB Smart Chain native token",
    networks: [
      { networkId: "bsc", decimals: 18 },
      { networkId: "bsc_testnet", decimals: 18 }
    ]
  },
  {
    id: "base",
    name: "Base",
    code: "BASE",
    symbol: "ETH",
    type: "native",
    decimals: 18,
    color: "#0052FF",
    description: "Base layer-2 network by Coinbase",
    networks: [
      { networkId: "base", decimals: 18 },
      { networkId: "base_sepolia", decimals: 18 }
    ]
  },
  {
    id: "usdt",
    name: "Tether USD",
    code: "USDT",
    symbol: "USDT",
    type: "erc20",
    decimals: 6,
    color: "#26a17b",
    badge: "Stablecoin",
    description: "USD-pegged stablecoin",
    networks: [
      {
        networkId: "localhost",
        contractAddress: "0xaA8E23Fb1079EA71e0a56F48a2aA51851D8433D0",
        decimals: 6
      },
      {
        networkId: "ethereum",
        contractAddress: "0xdac17f958d2ee523a2206206994597c13d831ec7",
        decimals: 6
      },
      {
        networkId: "sepolia",
        contractAddress: "0x7169D38820dfd117C3FA1f22a697dBA58d90BA06",
        decimals: 6
      }
    ]
  },
  {
    id: "usdc",
    name: "USD Coin",
    code: "USDC",
    symbol: "USDC",
    type: "erc20",
    decimals: 6,
    color: "#2775ca",
    badge: "Stablecoin",
    description: "USD-backed stablecoin by Circle",
    networks: [
      {
        networkId: "localhost",
        contractAddress: "0x5FbDB2315678afecb367f032d93F642f64180aa3",
        decimals: 6
      },
      {
        networkId: "ethereum",
        contractAddress: "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48",
        decimals: 6
      },
      {
        networkId: "sepolia",
        contractAddress: "0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238",
        decimals: 6
      },
      {
        networkId: "base_sepolia",
        contractAddress: "0x036cbd53842c5426634e7929541ec2318f3dcf7e",
        decimals: 6
      },
      {
        networkId: "base",
        contractAddress: "0x833589fcd6edb6e08f4c7c32d4f71b54bda02913",
        decimals: 6
      }
    ]
  },
  {
    id: "cbbtc",
    name: "Coinbase Wrapped BTC",
    code: "cbBTC",
    symbol: "cbBTC",
    type: "erc20",
    decimals: 8,
    color: "#F7931A",
    badge: "Wrapped BTC",
    description: "Bitcoin backed 1:1 by Coinbase",
    networks: [
      {
        networkId: "ethereum",
        contractAddress: "0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf",
        decimals: 8
      },
      {
        networkId: "base",
        contractAddress: "0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf",
        decimals: 8
      }
    ]
  }
];
var NETWORK_INDEX = new Map(SUPPORTED_NETWORKS.map((network) => [network.id, network]));
var DEFAULT_NETWORK_FILTER = {
  includeLocal: true,
  includeMainnets: true,
  includeTestnets: true
};
function resolveAllowedNetworkIds(options) {
  const mergedOptions = {
    ...DEFAULT_NETWORK_FILTER,
    ...options
  };
  const allowed = /* @__PURE__ */ new Set();
  for (const network of SUPPORTED_NETWORKS) {
    if (network.category === "mainnet" && !mergedOptions.includeMainnets) {
      continue;
    }
    if (network.category === "testnet" && !mergedOptions.includeTestnets) {
      continue;
    }
    if (network.category === "local" && !mergedOptions.includeLocal) {
      continue;
    }
    allowed.add(network.id);
  }
  if (options?.networkIds && options.networkIds.length > 0) {
    const requested = new Set(options.networkIds);
    for (const networkId of Array.from(allowed)) {
      if (!requested.has(networkId)) {
        allowed.delete(networkId);
      }
    }
  }
  return allowed;
}
function cloneNetworkConfig(network) {
  const { category: _category, ...rest } = network;
  return { ...rest };
}
function cloneAssetNetworkConfig(network) {
  return { ...network };
}
function cloneAssetConfig(asset, networks) {
  return {
    ...asset,
    networks
  };
}
function getSupportedNetworks(options) {
  const allowedNetworkIds = resolveAllowedNetworkIds(options);
  return SUPPORTED_NETWORKS.filter((network) => allowedNetworkIds.has(network.id)).map(
    (network) => cloneNetworkConfig(network)
  );
}
function getNetworkById(networkId) {
  const record = NETWORK_INDEX.get(networkId);
  return record ? cloneNetworkConfig(record) : null;
}
function getSupportedAssets(options) {
  const allowedNetworkIds = resolveAllowedNetworkIds(options);
  return SUPPORTED_ASSETS.reduce((result, asset) => {
    const filteredNetworks = asset.networks.filter((network) => allowedNetworkIds.has(network.networkId)).map((network) => cloneAssetNetworkConfig(network));
    if (filteredNetworks.length > 0) {
      result.push(cloneAssetConfig(asset, filteredNetworks));
    }
    return result;
  }, []);
}
function getAssetById(assetId, options) {
  const allowedNetworkIds = resolveAllowedNetworkIds(options);
  const asset = SUPPORTED_ASSETS.find((item) => item.id === assetId.toLowerCase());
  if (!asset) {
    return null;
  }
  const filteredNetworks = asset.networks.filter((network) => allowedNetworkIds.has(network.networkId)).map((network) => cloneAssetNetworkConfig(network));
  if (filteredNetworks.length === 0) {
    return null;
  }
  return cloneAssetConfig(asset, filteredNetworks);
}
function getDefaultAsset(options) {
  const assets = getSupportedAssets(options);
  return assets.length > 0 ? assets[0] : null;
}

// src/core/index.ts
function createWalletKit() {
  return {
    async generateWallet(config) {
      return generateWallet(config);
    },
    async importMnemonic(mnemonic, config) {
      return importMnemonic(mnemonic, config);
    },
    async exportMnemonic(wallet, opts) {
      return exportMnemonic(wallet, opts);
    },
    async secureStore(wallet, opts) {
      return secureStore(wallet, opts?.ttl);
    },
    async readStored(walletId) {
      return readStored(walletId);
    },
    async wipeStored(walletId) {
      await wipeStored(walletId);
      emitEvent({ type: "wipe-complete", payload: { walletId } });
    },
    on(event, handler) {
      return onEvent(event, handler);
    },
    emit(event) {
      emitEvent(event);
    }
  };
}
export {
  DEFAULT_PIN_LENGTH,
  DEFAULT_RPC_URLS,
  PBKDF2_ITERATIONS,
  SAFE_EMOJI,
  generateWallet as createWallet,
  createWalletKit,
  createWalletWithClaim,
  decodeBase58,
  decryptWithEmojiPin,
  decryptWithEmojiPin as decryptWithPin,
  wipeStored as deleteWallet,
  emitEvent,
  encodeBase58,
  encryptWithEmojiPin,
  encryptWithEmojiPin as encryptWithPin,
  estimateGas,
  executeGaslessWithdrawal,
  exportMnemonic,
  generateEmojiPin,
  generateQr,
  generateWallet,
  getAddress,
  getAssetById,
  getBalance,
  getDefaultAsset,
  exportMnemonic as getMnemonic,
  getNetworkById,
  getNextNonce,
  getSupportedAssets,
  getSupportedNetworks,
  getTokenBalance,
  hasWallet,
  importFromMnemonic,
  importMnemonic,
  importMnemonic as importWallet,
  isERC20Contract,
  readStored as loadWallet,
  onEvent,
  readStored,
  sanitizeMnemonic,
  secureStore as saveWallet,
  secureStore,
  signPermit,
  signWithdrawIntent,
  transferAll,
  transferToken,
  validateAddress,
  validateEmojiPin,
  wipeStored
};
